/**
 * PersonaldatenSection.jsx
 * Vollständiger Personalbogen-Editor direkt in der Mitarbeiterakte.
 * Admin kann alle Felder ausfüllen und:
 *   - Als Entwurf speichern
 *   - Abschließen (Admin unterschreibt Pflicht, MA optional sofort Vor Ort)
 *   - MA nachträglich Vor Ort unterschreiben lassen
 *   - Link an Mitarbeiter senden (neuer Flow, unverändert)
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import SignaturePad from './ui/SignaturePad';
import { KRANKENKASSEN, KVTypSelect, AddressAutocomplete, GermanDateInput } from './PersonalboqenSmartFields';
import { PersonalboqenAbschlussModal, MitarbeiterNachtragsSignModal } from './PersonalboqenAbschlussModal';
import {
  Save, Send, PenLine, ChevronDown, ChevronUp, Copy, Check,
  User, Building2, Euro, Shield, Landmark, FileText, Loader2, X,
  AlertTriangle, CheckCircle2, Clock, FileDown, ArrowRightLeft, ShieldCheck,
  MessageCircleQuestion, Megaphone, ClipboardCheck, Circle, AlertCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BESCHAEFTIGUNGSART = [
  { value: 'vollzeit',   label: 'Vollzeit' },
  { value: 'teilzeit',   label: 'Teilzeit' },
  { value: 'minijob',    label: 'Aushilfe (Minijob)' },
  { value: 'werkstudent', label: 'Werkstudent' },
];

const STEUERKLASSEN = ['1','2','3','4','5','6'];

const FAMILIENSTAND = [
  { value: 'ledig',            label: 'Ledig' },
  { value: 'verheiratet',      label: 'Verheiratet' },
  { value: 'geschieden',       label: 'Geschieden' },
  { value: 'verwitwet',        label: 'Verwitwet' },
  { value: 'getrennt',         label: 'Getrennt lebend' },
  { value: 'lebenspartnerschaft', label: 'Eingetragene Lebenspartnerschaft' },
];

const KONFESSION = [
  { value: 'ev',  label: 'Evangelisch' },
  { value: 'rk',  label: 'Römisch-Katholisch' },
  { value: 'other', label: 'Andere kirchensteuerpflichtige Konfession' },
  { value: 'none', label: 'Keine / kirchensteuerfrei' },
];

function SectionHeader({ icon: Icon, title, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 bg-muted/40 hover:bg-muted/60 rounded-xl transition-colors"
    >
      <div className="flex items-center gap-2 font-semibold text-sm">
        <Icon className="h-4 w-4 text-primary" />
        {title}
      </div>
      {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
    </button>
  );
}

function Field({ label, children, required, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
        {hint && <span className="text-muted-foreground font-normal ml-1">({hint})</span>}
      </Label>
      {children}
    </div>
  );
}

export function PersonaldatenSection({ employee, headers, onBeschaeftigungsWechsel }) {
  const [bogen, setBogen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinCopied, setPinCopied] = useState(false);

  // Section open/close
  const [openAG, setOpenAG] = useState(false);
  const [openPersonal, setOpenPersonal] = useState(false);
  const [openBank, setOpenBank] = useState(false);
  const [openSteuer, setOpenSteuer] = useState(false);

  // Form data
  const [ag, setAg] = useState({});
  const [personal, setPersonal] = useState({});
  const [bank, setBank] = useState({});
  const [steuer, setSteuer] = useState({});

  // Neue Abschluss-Modals
  const [showAbschlussModal, setShowAbschlussModal] = useState(false);
  const [showNachtragsModal, setShowNachtragsModal] = useState(false);

  // Arbeitgeber-Rückfrage (konkrete Nachbesserung am selben Vorgang)
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoText, setInfoText] = useState('');
  const [infoSending, setInfoSending] = useState(false);

  // Arbeitgeber-Abschluss (getrennte Zustände)
  const [abschluss, setAbschluss] = useState(null);
  const [abschlussOpen, setAbschlussOpen] = useState(false);
  const [abschlussBusy, setAbschlussBusy] = useState(false);

  // Alte Modal states (für Link-Versand Flow – unverändert)
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizeStep, setFinalizeStep] = useState('choose');
  const [sigMA, setSigMA] = useState(null);
  const [sigAG, setSigAG] = useState(null);
  const [sigMAEmpty, setSigMAEmpty] = useState(true);
  const [sigAGEmpty, setSigAGEmpty] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const sigMARef = useRef(null);
  const sigAGRef = useRef(null);

  // RV-Befreiungsantrag State
  const [rvMerkblatt, setRvMerkblatt] = useState(false);
  const [rvSigMA, setRvSigMA] = useState(null);
  const [rvSigAG, setRvSigAG] = useState(null);
  const [rvSigMAEmpty, setRvSigMAEmpty] = useState(true);
  const [rvSigAGEmpty, setRvSigAGEmpty] = useState(true);
  const [rvFinalizing, setRvFinalizing] = useState(false);
  const rvSigMARef = useRef(null);
  const rvSigAGRef = useRef(null);

  useEffect(() => {
    loadBogen();
  }, [employee?.id]);

  const loadBogen = async () => {
    setLoading(true);
    // Führende aktuelle Adresse (users) in strukturierte Bogen-Felder zerlegen
    const splitStrasse = (c) => {
      c = (c || '').trim();
      if (!c) return ['', ''];
      const i = c.lastIndexOf(' ');
      if (i > 0) { const last = c.slice(i + 1); if (/\d/.test(last)) return [c.slice(0, i).trim(), last.trim()]; }
      return [c, ''];
    };
    const splitPlzOrt = (c) => {
      c = (c || '').trim();
      if (!c) return ['', ''];
      const i = c.indexOf(' ');
      if (i > 0) { const first = c.slice(0, i); if (/^\d+$/.test(first)) return [first, c.slice(i + 1).trim()]; }
      return ['', c];
    };
    try {
      const res = await axios.get(`${API}/api/personalbogen/by-employee/${employee.id}`, { headers });
      const doc = res.data;
      setBogen(doc);

      // AG-Daten: berufsbezeichnung Fallback auf ausgeubte_taetigkeit
      const agRaw = doc.arbeitgeber || {};
      if (!agRaw.berufsbezeichnung && agRaw.ausgeubte_taetigkeit) {
        agRaw.berufsbezeichnung = agRaw.ausgeubte_taetigkeit;
      }
      setAg(agRaw);

      // Personal-Daten: Telefon-Normalisierung (führende 0 ergänzen wenn fehlt)
      const personalRaw = doc.personal || {};
      if (personalRaw.telefon) {
        const tel = String(personalRaw.telefon).trim();
        // Wenn Nummer mit 1 beginnt (z.B. 151...) und kein +/0 vorangestellt → "0" ergänzen
        if (/^[1-9]/.test(tel) && !tel.startsWith('+') && !tel.startsWith('0')) {
          personalRaw.telefon = '0' + tel;
        }
      }
      setPersonal(personalRaw);
      // Führende aktuelle Adresse (users) hat Vorrang, falls der Bogen (noch) keine hat
      if (!(personalRaw.strasse || '').trim() && (employee.strasse || '').trim()) {
        const [s, h] = splitStrasse(employee.strasse);
        personalRaw.strasse = s; if (!(personalRaw.hausnummer || '').trim()) personalRaw.hausnummer = h;
      }
      if (!(personalRaw.plz || '').trim() && (employee.plz_ort || '').trim()) {
        const [plz, ort] = splitPlzOrt(employee.plz_ort);
        personalRaw.plz = plz; personalRaw.ort = ort;
      }
      setPersonal(personalRaw);
      setBank(doc.bank || {});
      setSteuer(doc.steuer || {});
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error('Fehler beim Laden der Personaldaten');
      }
      // Pre-fill from employee (inkl. führender aktueller Adresse)
      const [fs, fh] = splitStrasse(employee.strasse);
      const [fplz, fort] = splitPlzOrt(employee.plz_ort);
      setPersonal({
        vorname: employee.name?.split(' ')[0] || '',
        familienname: employee.name?.split(' ').slice(1).join(' ') || '',
        email: employee.email || '',
        strasse: fs,
        hausnummer: fh,
        plz: fplz,
        ort: fort,
      });
    } finally {
      setLoading(false);
    }
  };

  const isMinijob = ag.arbeitszeit === 'minijob';

  const buildPayload = () => ({
    arbeitgeber: ag,
    personal,
    bank,
    steuer,
  });

  const persistDraft = async (showToast = true) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/personalbogen/by-employee/${employee.id}`,
        buildPayload(),
        { headers }
      );
      setBogen(res.data);
      // Teil-Fehlschlag ehrlich melden: wurde der Bogen gespeichert, aber die führende
      // aktuelle Adresse nicht übernommen, NICHT als vollen Erfolg darstellen.
      if (res.data?.address_sync_warning) {
        toast.warning(res.data.address_sync_warning, { duration: 8000 });
      } else if (showToast) {
        toast.success('Personaldaten als Entwurf gespeichert');
      }
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern – Eingaben wurden NICHT gespeichert');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => persistDraft(true);

  // ── Arbeitgeber-Abschluss: getrennte Zustände laden ──────────────────
  const loadAbschluss = async (bid) => {
    const id = bid || bogen?.id;
    if (!id) return;
    try {
      const res = await axios.get(`${API}/api/personalbogen/${id}/abschluss-status`, { headers });
      setAbschluss(res.data);
    } catch (e) { /* lesend – stillschweigend */ }
  };

  useEffect(() => {
    if (bogen?.id && ['submitted', 'info_requested', 'admin_filled', 'approved', 'completed', 'finalized'].includes(bogen.status)) {
      loadAbschluss(bogen.id);
    }
  }, [bogen?.id, bogen?.status, bogen?.signature_arbeitnehmer, bogen?.signature_arbeitgeber]);

  // Konkrete Rückfrage an den Mitarbeiter (gleicher Vorgang/ID, kein Mailversand)
  const handleRequestInfo = async () => {
    const msg = infoText.trim();
    if (!msg) { toast.error('Bitte eine konkrete Rückfrage formulieren.'); return; }
    setInfoSending(true);
    try {
      const res = await axios.post(`${API}/api/personalbogen/${bogen.id}/request-info`, { message: msg }, { headers });
      setBogen(res.data);
      setShowInfoModal(false);
      setInfoText('');
      toast.success('Rückfrage gesendet – Mitarbeiter kann über denselben Link/PIN gezielt ergänzen.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rückfrage konnte nicht gesendet werden.');
    } finally {
      setInfoSending(false);
    }
  };

  const handleToggleReviewed = async (done) => {
    setAbschlussBusy(true);
    try {
      const res = await axios.post(`${API}/api/personalbogen/${bogen.id}/mark-data-reviewed`, { done }, { headers });
      setBogen(res.data);
      await loadAbschluss(bogen.id);
      toast.success(done ? 'Datenprüfung als erledigt markiert.' : 'Datenprüfung zurückgesetzt.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen.');
    } finally { setAbschlussBusy(false); }
  };

  const handleToggleReported = async (done) => {
    setAbschlussBusy(true);
    try {
      const res = await axios.post(`${API}/api/personalbogen/${bogen.id}/mark-reported`, { done }, { headers });
      setBogen(res.data);
      await loadAbschluss(bogen.id);
      toast.success(done ? 'Externe Meldung als erledigt markiert.' : 'Externe Meldung zurückgesetzt.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen.');
    } finally { setAbschlussBusy(false); }
  };

  // Speichern & Abschließen: ZUERST Feldwerte persistieren, NUR bei Erfolg zum Signieren.
  // Bei Speicherfehler: kein Abschluss, Eingaben bleiben, Fehler sichtbar.
  const handleSaveAndFinalize = async () => {
    const ok = await persistDraft(false);
    if (ok) setShowAbschlussModal(true);
  };

  const handleRvVorOrt = async () => {
    if (!rvSigMA || !rvSigAG) { toast.error('Beide Unterschriften werden benötigt'); return; }
    setRvFinalizing(true);
    try {
      await axios.put(
        `${API}/api/personalbogen/by-employee/${employee.id}/rv-antrag-vor-ort`,
        {
          merkblatt_gelesen: rvMerkblatt,
          ort_an: personal.ort || '',
          ort_ag: personal.ort || '',
          signature_an: rvSigMA,
          signature_ag: rvSigAG,
          datum: new Date().toISOString().slice(0, 10),
        },
        { headers }
      );
      await loadBogen();
      setFinalizeStep('rv-success');
      toast.success('RV-Befreiungsantrag erfolgreich unterschrieben!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern des Befreiungsantrags');
    } finally {
      setRvFinalizing(false);
    }
  };

  const handleFinalizeVorOrt = async () => {
    if (!sigMA || !sigAG) {
      toast.error('Beide Unterschriften werden benötigt');
      return;
    }
    setFinalizing(true);
    try {
      await axios.post(
        `${API}/api/personalbogen/by-employee/${employee.id}/finalize-vor-ort`,
        {
          ...buildPayload(),
          signature_arbeitnehmer: sigMA,
          signature_arbeitgeber: sigAG,
          datum_unterschrift: new Date().toISOString().slice(0, 10),
        },
        { headers }
      );
      await loadBogen();
      setFinalizeStep('success');
      toast.success('Personalbogen erfolgreich abgeschlossen!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Abschließen');
    } finally {
      setFinalizing(false);
    }
  };

  const handleSendLink = async () => {
    setSendingLink(true);
    try {
      // Erst speichern
      await axios.put(`${API}/api/personalbogen/by-employee/${employee.id}`, buildPayload(), { headers });
      const updated = await axios.get(`${API}/api/personalbogen/by-employee/${employee.id}`, { headers });
      const doc = updated.data;
      // Link-Email ohne PIN senden (PIN zeigen wir dem Admin)
      const email = personal.email || employee.email;
      if (!email) {
        toast.error('Keine E-Mail-Adresse hinterlegt');
        setSendingLink(false);
        return;
      }
      await axios.post(
        `${API}/api/personalbogen/public/${doc.token}/send-pin-email`,
        { email },
        { headers }
      );
      setBogen(doc);
      setFinalizeStep('link-sent');
      toast.success('Link an Mitarbeiter gesendet (ohne PIN – bitte mündlich mitteilen)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingLink(false);
    }
  };

  const copyPin = () => {
    if (bogen?.pin_code) {
      navigator.clipboard.writeText(bogen.pin_code);
      setPinCopied(true);
      setTimeout(() => setPinCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isFinished = bogen?.status === 'admin_filled' || bogen?.status === 'submitted';

  return (
    <div className="space-y-3">
      {/* Status Banner */}
      {isFinished && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-3 flex items-center gap-2">
          <Check className="h-4 w-4 text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800 dark:text-green-300">Personalbogen abgeschlossen</p>
            <p className="text-xs text-green-700 dark:text-green-400">
              Unterschrieben am {(() => {
                const raw = bogen?.datum_unterschrift || bogen?.datum_unterschrift_ag || bogen?.admin_signed_at || bogen?.submitted_at;
                if (!raw) return '–';
                if (/^\d{2}\.\d{2}\.\d{4}/.test(raw)) return raw.slice(0, 10);
                return new Date(raw).toLocaleDateString('de-DE');
              })()}
            </p>
          </div>
          <Button size="sm" variant="outline" className="text-xs gap-1 shrink-0" onClick={loadBogen}>
            <FileText className="h-3 w-3" /> Bearbeiten
          </Button>
        </div>
      )}

      {/* PIN-Anzeige wenn Bogen vorhanden */}
      {bogen?.pin_code && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 rounded-xl p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs text-amber-700 font-semibold">PIN für Mitarbeiter (mündlich mitteilen – DSGVO)</p>
              <p className="font-mono font-bold text-lg text-amber-900 dark:text-amber-300 tracking-widest mt-0.5">
                {bogen.pin_code}
              </p>
            </div>
            <button onClick={copyPin} className="p-2 rounded-lg hover:bg-amber-100 transition-colors">
              {pinCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-amber-700" />}
            </button>
          </div>
        </div>
      )}

      {/* ── SEKTION 1: ARBEITGEBER-DATEN ────────────────────────────────── */}
      <SectionHeader icon={Building2} title="Arbeitgeber-Daten" open={openAG} onToggle={() => setOpenAG(v => !v)} />
      {openAG && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-1 pb-2">
          <Field label="Eintrittsdatum" required>
            <Input type="date" value={ag.eintrittsdatum || ''} onChange={e => setAg(p => ({...p, eintrittsdatum: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Berufsbezeichnung" required>
            <Input
              placeholder="z.B. Tankwart, Kassierer"
              value={ag.berufsbezeichnung || ''}
              onChange={e => setAg(p => ({...p, berufsbezeichnung: e.target.value}))}
              style={{fontSize:'16px'}}
            />
          </Field>
          <Field label="Ausgeübte Tätigkeit" hint="z.B. Tanken, Kassieren, Reinigung">
            <Input
              placeholder="z.B. Tankstellenservicekraft"
              value={ag.ausgeubte_taetigkeit || ''}
              onChange={e => setAg(p => ({...p, ausgeubte_taetigkeit: e.target.value}))}
              style={{fontSize:'16px'}}
            />
          </Field>
          <Field label="Beschäftigungsart" required>
            <select value={ag.arbeitszeit || ''} onChange={e => setAg(p => ({...p, arbeitszeit: e.target.value}))}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm">
              <option value="">Bitte wählen…</option>
              {BESCHAEFTIGUNGSART.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </Field>
          <Field label="Lohnart">
            <select value={ag.lohnart || ''} onChange={e => setAg(p => ({...p, lohnart: e.target.value}))}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm">
              <option value="">Bitte wählen…</option>
              <option value="stundenlohn">Stundenlohn</option>
              <option value="monatslohn">Monatslohn</option>
            </select>
          </Field>
          {ag.lohnart === 'stundenlohn' && (
            <Field label="Stundenlohn (€)">
              <Input placeholder="z.B. 13,50" value={ag.stundenlohn || ''} onChange={e => setAg(p => ({...p, stundenlohn: e.target.value}))} inputMode="decimal" style={{fontSize:'16px'}} />
            </Field>
          )}
          {ag.lohnart === 'monatslohn' && (
            <Field label="Monatslohn (€)">
              <Input placeholder="z.B. 2200,00" value={ag.monatslohn || ''} onChange={e => setAg(p => ({...p, monatslohn: e.target.value}))} inputMode="decimal" style={{fontSize:'16px'}} />
            </Field>
          )}
          <Field label="Wochenstunden" required>
            <Input placeholder="z.B. 40" value={ag.stunden_pro_woche || ''} onChange={e => setAg(p => ({...p, stunden_pro_woche: e.target.value}))} inputMode="decimal" style={{fontSize:'16px'}} />
          </Field>
          <Field label="Monatsstunden">
            <Input placeholder="z.B. 173,33" value={ag.stunden_pro_monat || ''} onChange={e => setAg(p => ({...p, stunden_pro_monat: e.target.value}))} inputMode="decimal" style={{fontSize:'16px'}} />
          </Field>
          <Field label="Arbeitstage/Woche" hint="für Urlaubsberechnung">
            <select value={ag.arbeitstage_woche || ''} onChange={e => setAg(p => ({...p, arbeitstage_woche: e.target.value}))}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm">
              <option value="">Bitte wählen…</option>
              {['1','2','3','4','5','6','7'].map(d => <option key={d} value={d}>{d} Tag{d !== '1' ? 'e' : ''}</option>)}
            </select>
          </Field>
          <Field label="Urlaub (Tage/Jahr)" hint="auto-berechnet">
            <Input placeholder="z.B. 24" value={ag.urlaubstage || ''} onChange={e => setAg(p => ({...p, urlaubstage: e.target.value}))} inputMode="numeric" style={{fontSize:'16px'}} />
          </Field>
          <div className="flex items-center gap-3 col-span-full">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!ag.probezeit} onChange={e => setAg(p => ({...p, probezeit: e.target.checked}))} className="rounded" />
              Probezeit
            </label>
            {ag.probezeit && (
              <Input placeholder="Dauer z.B. 6 Monate" className="flex-1" value={ag.probezeit_dauer || ''} onChange={e => setAg(p => ({...p, probezeit_dauer: e.target.value}))} style={{fontSize:'16px'}} />
            )}
          </div>
          <div className="flex items-center gap-3 col-span-full">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!ag.befristet} onChange={e => setAg(p => ({...p, befristet: e.target.checked}))} className="rounded" />
              Befristung
            </label>
            {ag.befristet && (
              <Input type="date" className="flex-1" value={ag.befristung_bis || ''} onChange={e => setAg(p => ({...p, befristung_bis: e.target.value}))} />
            )}
          </div>
        </div>
      )}

      {/* ── SEKTION 2: PERSÖNLICHE DATEN ──────────────────────────────── */}
      <SectionHeader icon={User} title="Persönliche Daten" open={openPersonal} onToggle={() => setOpenPersonal(v => !v)} />
      {openPersonal && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-1 pb-2">
          <Field label="Vorname" required>
            <Input value={personal.vorname || ''} onChange={e => setPersonal(p => ({...p, vorname: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Familienname" required>
            <Input value={personal.familienname || ''} onChange={e => setPersonal(p => ({...p, familienname: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Geburtsname" hint="optional">
            <Input value={personal.geburtsname || ''} onChange={e => setPersonal(p => ({...p, geburtsname: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Geschlecht" required>
            <div className="flex gap-2">
              {[{v:'männlich',l:'Herr'},{v:'weiblich',l:'Frau'},{v:'diverse',l:'Divers'}].map(g => (
                <button key={g.v} type="button" onClick={() => setPersonal(p => ({...p, geschlecht: g.v}))}
                  className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all ${personal.geschlecht === g.v ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                  {g.l}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Adresse" required>
            <AddressAutocomplete
              strasse={personal.strasse || ''}
              hausnummer={personal.hausnummer || ''}
              plz={personal.plz || ''}
              ort={personal.ort || ''}
              onChange={addr => setPersonal(p => ({...p, ...addr}))}
            />
          </Field>
          <Field label="Geburtsdatum" required>
            <GermanDateInput value={personal.geburtsdatum} onChange={(iso) => setPersonal(p => ({...p, geburtsdatum: iso}))} data-testid="personal-geburtsdatum" />
          </Field>
          <Field label="Geburtsort" required>
            <Input value={personal.geburtsort || ''} onChange={e => setPersonal(p => ({...p, geburtsort: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Geburtsland" required>
            <Input placeholder="z.B. Deutschland, Türkei" value={personal.geburtsland || ''} onChange={e => setPersonal(p => ({...p, geburtsland: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Staatsangehörigkeit" required>
            <Input placeholder="z.B. deutsch, türkisch" value={personal.staatsangehoerigkeit || ''} onChange={e => setPersonal(p => ({...p, staatsangehoerigkeit: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Familienstand">
            <select value={personal.familienstand || ''} onChange={e => setPersonal(p => ({...p, familienstand: e.target.value}))}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm">
              <option value="">Bitte wählen…</option>
              {FAMILIENSTAND.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>
          <Field label="Telefon" required>
            <Input
              type="tel"
              value={personal.telefon || ''}
              onChange={e => {
                let val = e.target.value;
                // Automatisch "0" vorne ergänzen wenn User mit 1 beginnt (deutsche Handy-Nr.)
                setPersonal(p => ({...p, telefon: val}));
              }}
              onBlur={e => {
                const val = e.target.value.trim();
                if (val && /^[1-9]/.test(val) && !val.startsWith('+') && !val.startsWith('0')) {
                  setPersonal(p => ({...p, telefon: '0' + val}));
                }
              }}
              inputMode="tel"
              style={{fontSize:'16px'}}
              placeholder="z.B. 01511 123456"
            />
          </Field>
          <Field label="E-Mail" required>
            <Input type="email" value={personal.email || ''} onChange={e => setPersonal(p => ({...p, email: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <label className="flex items-center gap-2 text-sm col-span-full cursor-pointer">
            <input type="checkbox" checked={!!personal.schwerbehindert} onChange={e => setPersonal(p => ({...p, schwerbehindert: e.target.checked}))} className="rounded" />
            Schwerbehinderung (GdB ≥ 50)
          </label>
          {/* DSGVO Checkbox */}
          <label className="flex items-start gap-2 text-xs text-muted-foreground col-span-full cursor-pointer p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
            <input type="checkbox" checked={!!personal.dsgvo_consent} onChange={e => setPersonal(p => ({...p, dsgvo_consent: e.target.checked}))} className="rounded mt-0.5 shrink-0" />
            Ich willige ausdrücklich in die Verarbeitung meiner besonders schützenswerten Daten (Konfession, Schwerbehinderung) zum Zwecke der Lohnabrechnung ein.
          </label>
        </div>
      )}

      {/* ── SEKTION 3: BANK & VERSICHERUNG ────────────────────────────── */}
      <SectionHeader icon={Landmark} title="Bank & Versicherung" open={openBank} onToggle={() => setOpenBank(v => !v)} />
      {openBank && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-1 pb-2">
          <Field label="IBAN" required>
            <Input placeholder="DE00 0000 0000 0000 0000 00" value={bank.iban || ''} onChange={e => setBank(p => ({...p, iban: e.target.value.toUpperCase()}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="BIC">
            <Input value={bank.bic || ''} onChange={e => setBank(p => ({...p, bic: e.target.value.toUpperCase()}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="SV-Nummer (Sozialversicherung)">
            <Input placeholder="z.B. 65 010150 A 123" value={personal.sv_nummer || ''} onChange={e => setPersonal(p => ({...p, sv_nummer: e.target.value}))} style={{fontSize:'16px'}} />
          </Field>
          <Field label="Krankenkasse" required>
            <select value={personal.krankenkasse || ''} onChange={e => setPersonal(p => ({...p, krankenkasse: e.target.value}))}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm">
              <option value="">Bitte wählen…</option>
              {KRANKENKASSEN.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </Field>
          <div className="col-span-full">
            <KVTypSelect value={personal.kv_typ} onChange={v => setPersonal(p => ({...p, kv_typ: v}))} required />
          </div>
        </div>
      )}

      {/* ── SEKTION 4: STEUER ──────────────────────────────────────────── */}
      <SectionHeader icon={Euro} title="Steuerdaten" open={openSteuer} onToggle={() => setOpenSteuer(v => !v)} />
      {openSteuer && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-1 pb-2">
          <Field label="Steueridentifikationsnummer" required>
            <Input placeholder="11-stellige Steuer-ID" value={steuer.steuer_id || ''} onChange={e => setSteuer(p => ({...p, steuer_id: e.target.value}))} inputMode="numeric" style={{fontSize:'16px'}} />
          </Field>
          <Field label="Steuerklasse" required>
            <div className="flex gap-1.5">
              {STEUERKLASSEN.map(k => (
                <button key={k} type="button" onClick={() => setSteuer(p => ({...p, steuerklasse: k}))}
                  className={`flex-1 py-2 rounded-lg border text-sm font-bold transition-all ${steuer.steuerklasse === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                  {k}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Kinderfreibeträge">
            <Input placeholder="z.B. 0,5" value={steuer.kinderfreibetraege || ''} onChange={e => setSteuer(p => ({...p, kinderfreibetraege: e.target.value}))} inputMode="decimal" style={{fontSize:'16px'}} />
          </Field>
          <Field label="Konfession">
            <select value={steuer.konfession || ''} onChange={e => setSteuer(p => ({...p, konfession: e.target.value}))}
              className="h-10 w-full rounded-md border bg-background px-3 text-sm">
              <option value="">Bitte wählen…</option>
              {KONFESSION.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </Field>
        </div>
      )}

      {/* ── MINIJOB: RV-Befreiungsantrag Hinweis ──────────────────────── */}
      {isMinijob && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Aushilfe (Minijob): RV-Befreiungsantrag erforderlich</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Nach dem Abschluss des Personalbogens wird automatisch der Befreiungsantrag von der Rentenversicherungspflicht zur Unterschrift angezeigt.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── STATUS BADGE ────────────────────────────────────────────────── */}
      {bogen && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold ${
          bogen.status === 'approved'
            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300'
            : bogen.status === 'admin_filled'
            ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300'
            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
        }`}>
          {bogen.status === 'approved' ? (
            <><CheckCircle2 className="h-4 w-4" /> Vollständig – beide Unterschriften vorhanden</>
          ) : bogen.status === 'admin_filled' ? (
            <><Clock className="h-4 w-4" /> Admin hat unterschrieben – Mitarbeiter-Unterschrift ausstehend (optional)</>
          ) : bogen.status === 'submitted' ? (
            <><CheckCircle2 className="h-4 w-4 text-green-600" /> Mitarbeiter hat ausgefüllt und unterschrieben</>
          ) : bogen.status === 'info_requested' ? (
            <><MessageCircleQuestion className="h-4 w-4 text-amber-600" /> Rückfrage an Mitarbeiter gesendet – wartet auf erneute Einreichung</>
          ) : (
            <><Save className="h-4 w-4" /> Entwurf – noch nicht abgeschlossen</>
          )}
        </div>
      )}

      {/* ── VERSIONS-HINWEIS (eingefrorene unterschriebene Fassung) ──────── */}
      {bogen?.has_signed_version && bogen?.current_version_no && (
        <div className="space-y-1" data-testid="personalbogen-version-info">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/10 text-xs text-emerald-800 dark:text-emerald-300">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span data-testid="version-badge-text">
              Unterschriebene Fassung <strong>v{bogen.current_version_no}</strong>
              {bogen.version_locked_at ? ` · revisionssicher eingefroren am ${new Date(bogen.version_locked_at).toLocaleString('de-DE')}` : ''}
              {' '}— PDF-Downloads zeigen exakt diese unterschriebene Fassung.
            </span>
          </div>
          {bogen?.live_content_diverged_from_signed && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 text-xs text-amber-800 dark:text-amber-300" data-testid="version-diverged-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Aktuelle Daten wurden nach der Unterschrift geändert. Der Download zeigt weiterhin die zuletzt unterschriebene Fassung; für den neuen Stand ist eine neue Unterschrift erforderlich.</span>
            </div>
          )}
        </div>
      )}


      {/* ── GEPLANTER BESCHÄFTIGUNGSWECHSEL (noch nicht aktiv) ───────────── */}
      {bogen?.scheduled_employment_change && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10 text-xs text-blue-800 dark:text-blue-300" data-testid="scheduled-change-banner">
          <Clock className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Geplanter Beschäftigungswechsel zu <strong>{bogen.scheduled_employment_change.neue_beschaeftigungsart}</strong>
            {' '}ab <strong>{bogen.scheduled_employment_change.wirksam_ab}</strong> — wird am Gültigkeitsdatum
            automatisch übernommen. Aktuelle Daten, Rechte und Dokumente bleiben bis dahin unverändert.
          </span>
        </div>
      )}


      {/* ── OFFENE ARBEITGEBER-RÜCKFRAGE (an Mitarbeiter gesendet) ───────── */}
      {bogen?.status === 'info_requested' && bogen?.info_request?.message && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 text-xs text-amber-800 dark:text-amber-300" data-testid="open-info-request-banner">
          <MessageCircleQuestion className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Offene Rückfrage an den Mitarbeiter</p>
            <p className="mt-0.5 whitespace-pre-wrap">{bogen.info_request.message}</p>
            <p className="mt-1 opacity-80">Der Mitarbeiter ergänzt über denselben Link/PIN und reicht erneut ein. Historische unterschriebene Fassungen bleiben unverändert.</p>
          </div>
        </div>
      )}

      {/* ── ARBEITGEBER-ABSCHLUSS: getrennte Zustände ───────────────────── */}
      {abschluss && bogen && ['submitted', 'info_requested', 'admin_filled', 'approved', 'completed', 'finalized'].includes(bogen.status) && (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="abschluss-panel">
          <button type="button" onClick={() => setAbschlussOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/40 hover:bg-muted/70 transition-colors">
            <span className="flex items-center gap-2 text-sm font-semibold">
              <ClipboardCheck className="h-4 w-4 text-primary" />
              Arbeitgeber-Abschluss
              {abschluss.vollstaendig_abgeschlossen ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">vollständig &amp; gemeldet</span>
              ) : abschluss.abschluss_bereit ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">abschlussbereit – Meldung offen</span>
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">in Bearbeitung</span>
              )}
            </span>
            {abschlussOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
          {abschlussOpen && (
            <div className="p-3 space-y-2" data-testid="abschluss-states">
              {[
                { key: 'daten_geprueft', label: 'Datenprüfung durch Arbeitgeber', ok: abschluss.daten_geprueft },
                { key: 'mitarbeiter_signatur', label: 'Mitarbeiter-Unterschrift', ok: abschluss.mitarbeiter_signatur },
                { key: 'arbeitgeber_signatur', label: 'Arbeitgeber-Unterschrift', ok: abschluss.arbeitgeber_signatur },
                { key: 'dokumente', label: abschluss.offene_dokumente > 0 ? `Dokumente (${abschluss.offene_dokumente} offen)` : 'Dokumente vollständig', ok: abschluss.dokumente_vollstaendig },
                { key: 'rv', label: abschluss.rv_relevant ? `RV-Entscheidung/Antrag${abschluss.rv_wahl ? ` (${abschluss.rv_wahl})` : ''}` : 'RV nicht relevant', ok: abschluss.rv_ok },
                { key: 'extern', label: 'Externe Meldung (Lohnbüro/SV)', ok: abschluss.extern_gemeldet },
              ].map(row => (
                <div key={row.key} className="flex items-center gap-2 text-sm" data-testid={`abschluss-state-${row.key}`}>
                  {row.ok
                    ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <span className={row.ok ? 'text-foreground' : 'text-muted-foreground'}>{row.label}</span>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                <Button variant={abschluss.daten_geprueft ? 'outline' : 'default'} size="sm" disabled={abschlussBusy}
                  onClick={() => handleToggleReviewed(!abschluss.daten_geprueft)}
                  className="gap-1.5" data-testid="toggle-data-reviewed-btn">
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  {abschluss.daten_geprueft ? 'Datenprüfung zurücknehmen' : 'Datenprüfung erledigt'}
                </Button>
                <Button variant={abschluss.extern_gemeldet ? 'outline' : 'default'} size="sm"
                  disabled={abschlussBusy || !abschluss.abschluss_bereit}
                  onClick={() => handleToggleReported(!abschluss.extern_gemeldet)}
                  className="gap-1.5" data-testid="toggle-reported-btn"
                  title={!abschluss.abschluss_bereit ? 'Erst nach Datenprüfung, Unterschriften, Dokumenten und RV-Entscheidung meldbar' : ''}>
                  <Megaphone className="h-3.5 w-3.5" />
                  {abschluss.extern_gemeldet ? 'Meldung zurücknehmen' : 'Als extern gemeldet markieren'}
                </Button>
              </div>
              {!abschluss.abschluss_bereit && (
                <p className="text-[11px] text-muted-foreground pt-1">Die externe Meldung wird bewusst nicht automatisch aus „eingereicht" oder einer einzelnen Unterschrift abgeleitet – erst alle Teilschritte erledigen.</p>
              )}
            </div>
          )}
        </div>
      )}



      {/* ── ACTION BUTTONS ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        {/* Entwurf speichern (bestehend) */}
        <Button variant="outline" onClick={handleSaveDraft} disabled={saving} className="gap-1.5" data-testid="save-draft-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Entwurf speichern
        </Button>

        {/* Beschäftigungsart ändern — aus Personalbogen-Box hochgezogen */}
        {bogen && onBeschaeftigungsWechsel && (
          <Button variant="outline" onClick={onBeschaeftigungsWechsel}
            className="gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
            data-testid="change-employment-from-section-btn">
            <ArrowRightLeft className="h-4 w-4" />
            Beschäftigungsart ändern
          </Button>
        )}

        {/* PDF-Download — live, immer aktueller Stand */}
        {bogen && (
          <>
            <Button variant="outline" size="sm" className="gap-1.5"
              onClick={() => {
                const a = document.createElement('a');
                fetch(`${API}/api/personalbogen/${bogen.id}/pdf-form`, { headers })
                  .then(r => r.blob()).then(blob => {
                    a.href = URL.createObjectURL(blob);
                    a.download = `Personalbogen_${employee?.name || ''}.pdf`;
                    a.click();
                  });
              }}
              data-testid="pdf-download-section-btn">
              <FileDown className="h-3.5 w-3.5" />
              PDF
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5"
              onClick={() => {
                const a = document.createElement('a');
                fetch(`${API}/api/personalbogen/${bogen.id}/full-combined-pdf`, { headers })
                  .then(r => r.blob()).then(blob => {
                    a.href = URL.createObjectURL(blob);
                    a.download = `Akte_${employee?.name || ''}.pdf`;
                    a.click();
                  });
              }}
              data-testid="akte-download-section-btn">
              <FileDown className="h-3.5 w-3.5" />
              Akte
            </Button>
          </>
        )}

        {/* Abschließen (neuer Flow) */}
        {bogen?.status !== 'approved' && bogen?.status !== 'submitted' && (
          <Button onClick={handleSaveAndFinalize} disabled={saving} className="gap-1.5" data-testid="open-abschluss-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
            Speichern &amp; Abschließen
          </Button>
        )}

        {/* MA nachträglich unterschreiben */}
        {bogen?.status === 'admin_filled' && !bogen?.signature_arbeitnehmer && (
          <Button variant="outline" onClick={() => setShowNachtragsModal(true)}
            className="gap-1.5 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300"
            data-testid="open-nachtrags-btn">
            <User className="h-4 w-4" />
            MA unterschreibt Vor Ort
          </Button>
        )}

        {/* Rückfrage an Mitarbeiter (nur bei eingereichtem Bogen) */}
        {bogen?.status === 'submitted' && (
          <Button variant="outline" onClick={() => { setInfoText(''); setShowInfoModal(true); }}
            className="gap-1.5 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            data-testid="open-info-request-btn">
            <MessageCircleQuestion className="h-4 w-4" />
            Rückfrage an Mitarbeiter
          </Button>
        )}

        {/* Link senden (alter Flow, nur bei draft ohne disabled) */}
        {(!bogen?.link_disabled) && bogen?.status === 'draft' && (
          <Button variant="ghost" size="sm" onClick={() => { setShowFinalizeModal(true); setFinalizeStep('choose'); }}
            className="gap-1.5 text-muted-foreground">
            <Send className="h-4 w-4" />
            Link senden
          </Button>
        )}
      </div>

      {/* ── NEUE ABSCHLUSS-MODALS ────────────────────────────────────────── */}
      {showInfoModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !infoSending && setShowInfoModal(false)} data-testid="info-request-modal">
          <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} onClick={e => e.stopPropagation()}>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
                  <MessageCircleQuestion className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-bold text-base">Rückfrage an Mitarbeiter</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Der Mitarbeiter kann über denselben Link/PIN gezielt ergänzen und erneut einreichen. Es wird keine Mail versendet.</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Konkrete Rückfrage *</Label>
                <textarea rows={4} value={infoText} onChange={e => setInfoText(e.target.value)}
                  placeholder="z.B. Bitte die IBAN prüfen – die angegebene Nummer ist unvollständig."
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm" style={{ fontSize: '16px' }}
                  data-testid="info-request-textarea" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setShowInfoModal(false)} disabled={infoSending} data-testid="info-request-cancel-btn">Abbrechen</Button>
                <Button onClick={handleRequestInfo} disabled={infoSending || !infoText.trim()} className="gap-1.5" data-testid="info-request-send-btn">
                  {infoSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Rückfrage senden
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAbschlussModal && (
        <PersonalboqenAbschlussModal
          employee={employee}
          existingAgSignature={bogen?.signature_arbeitgeber || null}
          onClose={() => setShowAbschlussModal(false)}
          onDone={(updatedBogen) => {
            setBogen(updatedBogen);
            setShowAbschlussModal(false);
          }}
        />
      )}

      {showNachtragsModal && (
        <MitarbeiterNachtragsSignModal
          employee={employee}
          onClose={() => setShowNachtragsModal(false)}
          onDone={(updatedBogen) => {
            setBogen(updatedBogen);
            setShowNachtragsModal(false);
          }}
        />
      )}

      {/* ── ALTER FINALISIERUNGS-MODAL (Link-Versand) ───────────────────── */}
      {showFinalizeModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowFinalizeModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            onClick={e => e.stopPropagation()}>

            {/* SCHRITT: AUSWAHL */}
            {finalizeStep === 'choose' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Personalbogen fertigstellen</h2>
                  <button onClick={() => setShowFinalizeModal(false)} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">Wie möchtest du den Personalbogen abschließen?</p>
                <div className="grid gap-3">
                  <button onClick={() => setFinalizeStep('vor-ort-ma')}
                    className="flex items-center gap-3 p-4 border-2 border-primary/20 hover:border-primary bg-primary/5 rounded-xl text-left transition-all">
                    <PenLine className="h-6 w-6 text-primary shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">Vor Ort unterschreiben</p>
                      <p className="text-xs text-muted-foreground">Mitarbeiter und Admin unterschreiben jetzt direkt auf diesem Gerät</p>
                    </div>
                  </button>
                  <button onClick={() => { handleSendLink(); setFinalizeStep('link-loading'); }}
                    className="flex items-center gap-3 p-4 border-2 border-border hover:border-primary/40 rounded-xl text-left transition-all">
                    <Send className="h-6 w-6 text-blue-600 shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">Link an Mitarbeiter senden</p>
                      <p className="text-xs text-muted-foreground">Mitarbeiter erhält E-Mail mit vorausgefüllten Daten und unterschreibt selbst</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* SCHRITT: LINK LÄDT */}
            {finalizeStep === 'link-loading' && (
              <div className="p-6 flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">E-Mail wird gesendet...</p>
              </div>
            )}

            {/* SCHRITT: LINK GESENDET */}
            {finalizeStep === 'link-sent' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Link gesendet</h2>
                  <button onClick={() => setShowFinalizeModal(false)} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-medium text-green-800">E-Mail wurde an {personal.email || employee.email} gesendet.</p>
                  <p className="text-xs text-green-700">Der Mitarbeiter sieht alle vorausgefüllten Daten und muss nur noch unterschreiben.</p>
                </div>
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
                  <p className="text-xs text-amber-800 font-semibold mb-2">PIN – bitte mündlich mitteilen (DSGVO):</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-xl tracking-widest text-amber-900">{bogen?.pin_code}</span>
                    <button onClick={copyPin} className="p-2 rounded-lg hover:bg-amber-100">
                      {pinCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-amber-700" />}
                    </button>
                  </div>
                </div>
                <Button onClick={() => setShowFinalizeModal(false)} className="w-full">Schließen</Button>
              </div>
            )}

            {/* SCHRITT: VOR ORT – MITARBEITER UNTERSCHREIBT */}
            {finalizeStep === 'vor-ort-ma' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Unterschrift Mitarbeiter</h2>
                  <button onClick={() => setFinalizeStep('choose')} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Bitte übergib das Gerät an <strong>{personal.vorname} {personal.familienname}</strong> zum Unterschreiben.
                </p>
                <SignaturePad
                  ref={sigMARef}
                  onEmpty={setSigMAEmpty}
                  onSign={data => setSigMA(data)}
                  label={`${personal.vorname} ${personal.familienname}`}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { sigMARef.current?.clear(); setSigMA(null); setSigMAEmpty(true); }} className="gap-1">
                    <X className="h-4 w-4" /> Zurücksetzen
                  </Button>
                  <Button onClick={() => setFinalizeStep('vor-ort-ag')} disabled={sigMAEmpty || !sigMA} className="flex-1 gap-1">
                    <PenLine className="h-4 w-4" /> Weiter → Admin unterschreibt
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT: VOR ORT – ADMIN UNTERSCHREIBT */}
            {finalizeStep === 'vor-ort-ag' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Unterschrift Arbeitgeber</h2>
                  <button onClick={() => setFinalizeStep('vor-ort-ma')} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Jetzt bitte als Arbeitgeber/Admin unterschreiben.
                </p>
                <SignaturePad
                  ref={sigAGRef}
                  onEmpty={setSigAGEmpty}
                  onSign={data => setSigAG(data)}
                  label="Arbeitgeber / Admin"
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { sigAGRef.current?.clear(); setSigAG(null); setSigAGEmpty(true); }} className="gap-1">
                    <X className="h-4 w-4" /> Zurücksetzen
                  </Button>
                  <Button onClick={handleFinalizeVorOrt} disabled={sigAGEmpty || !sigAG || finalizing} className="flex-1 gap-1 bg-green-600 hover:bg-green-700">
                    {finalizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Personalbogen abschließen
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT: ERFOLG */}
            {finalizeStep === 'success' && (
              <div className="p-6 space-y-4 text-center">
                <div className="text-5xl">✅</div>
                <h2 className="text-lg font-bold">Personalbogen abgeschlossen!</h2>
                <p className="text-sm text-muted-foreground">
                  Beide Unterschriften wurden gespeichert. Der Personalbogen ist jetzt vollständig.
                </p>
                {isMinijob && (
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-left space-y-3">
                    <p className="text-sm font-semibold text-amber-900">Nächster Schritt: RV-Befreiungsantrag</p>
                    <p className="text-xs text-amber-800">
                      Da es sich um eine Aushilfstätigkeit (Minijob) handelt, muss der Mitarbeiter den
                      Befreiungsantrag von der Rentenversicherungspflicht unterschreiben.
                    </p>
                    <Button onClick={() => setFinalizeStep('rv-choose')} className="w-full gap-1.5 bg-amber-600 hover:bg-amber-700">
                      <PenLine className="h-4 w-4" /> Jetzt Befreiungsantrag unterschreiben
                    </Button>
                  </div>
                )}
                {!isMinijob && (
                  <Button onClick={() => { setShowFinalizeModal(false); loadBogen(); }} className="w-full">
                    Schließen
                  </Button>
                )}
              </div>
            )}

            {/* ── RV-BEFREIUNGSANTRAG FLOW ─────────────────────────────────── */}

            {/* SCHRITT: RV AUSWAHL */}
            {finalizeStep === 'rv-choose' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold">RV-Befreiungsantrag</h2>
                    <p className="text-xs text-muted-foreground">Befreiung von der Rentenversicherungspflicht (Minijob)</p>
                  </div>
                  <button onClick={() => { setShowFinalizeModal(false); loadBogen(); }} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                  Der Mitarbeiter muss bestätigen, dass er das Merkblatt über die Folgen der Befreiung gelesen hat,
                  und dann unterschreiben. Anschließend unterschreibt der Arbeitgeber.
                </div>
                <div className="grid gap-3">
                  <button onClick={() => setFinalizeStep('rv-vor-ort-info')}
                    className="flex items-center gap-3 p-4 border-2 border-primary/20 hover:border-primary bg-primary/5 rounded-xl text-left transition-all">
                    <PenLine className="h-6 w-6 text-primary shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">Jetzt Vor Ort unterschreiben</p>
                      <p className="text-xs text-muted-foreground">Mitarbeiter und Admin unterschreiben direkt auf diesem Gerät</p>
                    </div>
                  </button>
                  <button onClick={() => { setShowFinalizeModal(false); loadBogen(); toast.info('RV-Befreiungsantrag kann später im Personalbogen-Dashboard unterschrieben werden'); }}
                    className="flex items-center gap-3 p-4 border-2 border-border hover:border-primary/40 rounded-xl text-left transition-all">
                    <X className="h-6 w-6 text-muted-foreground shrink-0" />
                    <div>
                      <p className="font-semibold text-sm text-muted-foreground">Später erledigen</p>
                      <p className="text-xs text-muted-foreground">Im Personalbogen-Dashboard jederzeit nachholen</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* SCHRITT: RV – MERKBLATT + MITARBEITER */}
            {finalizeStep === 'rv-vor-ort-info' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Schritt 1: Merkblatt-Bestätigung</h2>
                  <button onClick={() => setFinalizeStep('rv-choose')} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Bitte übergib das Gerät an <strong>{personal.vorname} {personal.familienname}</strong>.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-2">
                  <p className="font-semibold">Wichtiger Hinweis für {personal.vorname}:</p>
                  <p className="text-xs leading-relaxed">
                    Mit diesem Antrag wirst du von der Rentenversicherungspflicht in deiner geringfügigen
                    Beschäftigung befreit. Das bedeutet, du zahlst keine eigenen RV-Beiträge mehr, hast aber auch
                    geringere Rentenansprüche. Diese Befreiung gilt für alle deine Minijobs.
                  </p>
                </div>
                <label className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-xl cursor-pointer">
                  <input type="checkbox" checked={!!rvMerkblatt} onChange={e => setRvMerkblatt(e.target.checked)} className="rounded mt-0.5 shrink-0 h-4 w-4" />
                  <span className="text-xs text-green-900 font-medium">
                    Ich bestätige, dass ich das Merkblatt über die Folgen der Befreiung von der
                    Rentenversicherungspflicht gelesen und verstanden habe.
                  </span>
                </label>
                <Button onClick={() => setFinalizeStep('rv-vor-ort-ma')} disabled={!rvMerkblatt} className="w-full gap-1">
                  <PenLine className="h-4 w-4" /> Weiter → Unterschrift Mitarbeiter
                </Button>
              </div>
            )}

            {/* SCHRITT: RV – UNTERSCHRIFT MITARBEITER */}
            {finalizeStep === 'rv-vor-ort-ma' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Unterschrift Mitarbeiter</h2>
                  <button onClick={() => setFinalizeStep('rv-vor-ort-info')} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">
                  <strong>{personal.vorname} {personal.familienname}</strong> – bitte jetzt unterschreiben:
                </p>
                <SignaturePad
                  ref={rvSigMARef}
                  onEmpty={setRvSigMAEmpty}
                  onSign={d => setRvSigMA(d)}
                  label={`${personal.vorname} ${personal.familienname}`}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { rvSigMARef.current?.clear(); setRvSigMA(null); setRvSigMAEmpty(true); }} className="gap-1">
                    <X className="h-4 w-4" /> Reset
                  </Button>
                  <Button onClick={() => setFinalizeStep('rv-vor-ort-ag')} disabled={rvSigMAEmpty || !rvSigMA} className="flex-1 gap-1">
                    <PenLine className="h-4 w-4" /> Weiter → Arbeitgeber
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT: RV – UNTERSCHRIFT ARBEITGEBER */}
            {finalizeStep === 'rv-vor-ort-ag' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">Unterschrift Arbeitgeber</h2>
                  <button onClick={() => setFinalizeStep('rv-vor-ort-ma')} className="p-1 rounded-full hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">Bitte als Arbeitgeber/Admin unterschreiben:</p>
                <SignaturePad
                  ref={rvSigAGRef}
                  onEmpty={setRvSigAGEmpty}
                  onSign={d => setRvSigAG(d)}
                  label="Arbeitgeber / Admin"
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { rvSigAGRef.current?.clear(); setRvSigAG(null); setRvSigAGEmpty(true); }} className="gap-1">
                    <X className="h-4 w-4" /> Reset
                  </Button>
                  <Button onClick={handleRvVorOrt} disabled={rvSigAGEmpty || !rvSigAG || rvFinalizing}
                    className="flex-1 gap-1 bg-green-600 hover:bg-green-700">
                    {rvFinalizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Befreiungsantrag abschließen
                  </Button>
                </div>
              </div>
            )}

            {/* SCHRITT: RV ERFOLG */}
            {finalizeStep === 'rv-success' && (
              <div className="p-6 space-y-4 text-center">
                <div className="text-5xl">🎉</div>
                <h2 className="text-lg font-bold">Alles erledigt!</h2>
                <p className="text-sm text-muted-foreground">
                  Personalbogen <strong>und</strong> RV-Befreiungsantrag wurden erfolgreich unterschrieben und gespeichert.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-left space-y-1">
                  <p className="font-semibold text-green-800">✅ Personalbogen – unterschrieben</p>
                  <p className="font-semibold text-green-800">✅ RV-Befreiungsantrag – unterschrieben</p>
                </div>
                <Button onClick={() => { setShowFinalizeModal(false); loadBogen(); }} className="w-full">
                  Schließen
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonaldatenSection;
